<template>
  <!-- 하나 바이블 공과 카드: 공과 정보(브로슈어) + 자료 꾸러미 -->
  <div class="w-full min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white">
    <div class="flex flex-col gap-0.5 bg-blue-50 px-4 py-3">
      <span v-if="eyebrow" class="text-xs font-bold text-amber-700">{{ eyebrow }}</span>
      <span class="text-xs text-neutral-500">{{ where }}</span>
      <strong class="text-base text-main-navy leading-snug">{{ lessonName(lesson) }} · {{ lesson.title }}</strong>
    </div>

    <dl class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 px-4 py-3 text-sm border-t border-neutral-100 break-words">
      <dt class="text-neutral-500">본문</dt>
      <dd>{{ lesson.passage }}</dd>
      <dt class="text-neutral-500">외울말씀</dt>
      <dd>{{ lesson.memoryVerseFull || lesson.memoryVerse }}</dd>
      <dt class="text-neutral-500">학습요점</dt>
      <dd>{{ lesson.keyPoint }}</dd>
    </dl>
    <p class="px-4 pb-3 text-xs text-neutral-400">출처: 총회 제111회기 홍보 브로슈어 {{ lesson.sourcePage }}쪽</p>

    <div v-if="showMaterials" class="flex flex-col gap-3 border-t border-neutral-100 px-4 py-3">
      <p class="text-xs font-bold text-amber-700">{{ groupLabel ? groupLabel + " " : "" }}수업 자료</p>
      <div v-if="loading" class="text-sm text-neutral-400">자료를 불러오는 중이에요…</div>
      <p v-else-if="!sections.length" class="text-sm text-neutral-500">
        {{ lesson.isSpecial ? "이 특별 공과 자료는 아직 올라오지 않았어요." : "이 공과에 연결된 자료를 아직 찾지 못했어요." }}
      </p>
      <div v-for="section in sections" v-else :key="section.division" class="flex flex-col gap-1.5">
        <h4 class="text-xs text-neutral-500">{{ section.label }} <span class="tabular-nums">{{ section.total }}</span></h4>
        <ChatContentList :items="section.items" />
      </div>
    </div>

    <div v-if="$slots.actions" class="flex flex-wrap gap-2 border-t border-neutral-100 px-4 py-3">
      <slot name="actions" />
    </div>

    <!-- 공유: 다른 선생님께, 부모님께 (F6) -->
    <div v-if="!loading" class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-neutral-100 px-4 py-2 text-xs text-neutral-500">
      <button type="button" class="hover:text-main-navy" @click="copyLink">{{ copied ? "링크를 복사했어요" : "링크 복사" }}</button>
      <span aria-hidden="true">·</span>
      <button type="button" class="hover:text-main-navy" @click="shareKakao">카카오톡 공유</button>
      <span aria-hidden="true">·</span>
      <button type="button" class="hover:text-main-navy" @click="emit('parent-notice')">부모님께 보낼 안내문</button>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref } from "vue";
import ChatContentList from "@/components/chatbot/ChatContentList.vue";
import { semesterName, lessonName } from "@/assets/js/hanaLessons";
import { shareUrlOf, copyText, shareToKakao } from "@/assets/js/chatShare";

const props = defineProps({
  lesson: { type: Object, required: true },
  // [{ division, label, items, total }] — null 이면 자료 영역을 숨긴다
  sections: { type: Array, default: null },
  loading: { type: Boolean, default: false },
  groupLabel: { type: String, default: "" },
  eyebrow: { type: String, default: "" },
});

const emit = defineEmits(["parent-notice"]);

const copied = ref(false);
const copyLink = async () => {
  if (!(await copyText(shareUrlOf(props.lesson)))) return;
  copied.value = true;
  setTimeout(() => (copied.value = false), 2000);
};

const shareKakao = async () => {
  const l = props.lesson;
  const text = `[하나 바이블] ${semesterName(l)} ${lessonName(l)} '${l.title}' 공과 정보와 수업 자료예요.`;
  // 카카오톡·공유창을 쓸 수 없으면 링크 복사로 대신한다
  if (!(await shareToKakao({ text, url: shareUrlOf(l) }))) await copyLink();
};

const showMaterials = computed(() => props.loading || Array.isArray(props.sections));

const where = computed(() => {
  const l = props.lesson;
  return semesterName(l) + (l.unit ? ` · ${l.unit}단원 ${l.unitTitle}` : ` · ${l.occasion || "특별 공과"}`);
});
</script>
